import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SecurityIcon from '@mui/icons-material/Security';
import {
  getContentSafetyScreeningSettings,
  updateContentSafetyScreeningSettings,
  getContentSafetyDefaultKeywords
} from '../../services/api';

const ContentSafetySettings = () => {
  const [settings, setSettings] = useState({
    enabled: true,
    screenJournalEntries: true,
    screenCheckInNotes: true,
    createAlertsOnCritical: true,
    createAlertsOnModerate: false,
    notifyTherapistImmediately: true,
    customKeywords: {
      critical: [],
      moderate: []
    }
  });

  const [defaultKeywords, setDefaultKeywords] = useState({
    critical: [],
    moderate: []
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [newKeyword, setNewKeyword] = useState({ critical: '', moderate: '' });

  useEffect(() => {
    fetchSettings();
    fetchDefaultKeywords();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const data = await getContentSafetyScreeningSettings(token);
      setSettings(data);
    } catch (err) {
      console.error('Error fetching content safety screening settings:', err);
      setError('Failed to load content safety screening settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultKeywords = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await getContentSafetyDefaultKeywords(token);
      setDefaultKeywords(data);
    } catch (err) {
      console.error('Error fetching default keywords:', err);
      // Non-blocking - defaults can still be shown even if this fails
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      const token = localStorage.getItem('token');
      const result = await updateContentSafetyScreeningSettings(settings, token);
      setSaveSuccess(true);
      if (result.changesLogged > 0) {
        console.log(`${result.changesLogged} changes logged to activity log`);
      }
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving content safety screening settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMainSwitch = (event) => {
    setSettings({
      ...settings,
      enabled: event.target.checked
    });
  };

  const handleToggleOption = (option) => (event) => {
    setSettings({
      ...settings,
      [option]: event.target.checked
    });
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : null);
  };

  const handleAddKeyword = (level) => {
    const keyword = newKeyword[level].trim().toLowerCase();
    if (!keyword) return;

    // Check if keyword already exists in defaults or custom
    if (defaultKeywords[level].includes(keyword) ||
        settings.customKeywords[level].includes(keyword)) {
      setError(`Keyword "${keyword}" already exists in ${level} keywords`);
      setTimeout(() => setError(null), 3000);
      return;
    }

    setSettings({
      ...settings,
      customKeywords: {
        ...settings.customKeywords,
        [level]: [...settings.customKeywords[level], keyword]
      }
    });
    setNewKeyword({ ...newKeyword, [level]: '' });
  };

  const handleRemoveKeyword = (level, keyword) => {
    setSettings({
      ...settings,
      customKeywords: {
        ...settings.customKeywords,
        [level]: settings.customKeywords[level].filter(k => k !== keyword)
      }
    });
  };

  const handleKeywordInputChange = (level) => (event) => {
    setNewKeyword({ ...newKeyword, [level]: event.target.value });
  };

  const handleKeywordKeyPress = (level) => (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddKeyword(level);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Box display="flex" alignItems="center">
            <SecurityIcon sx={{ mr: 1 }} />
            Content Safety Screening
          </Box>
        }
        subheader="Automatically screen journal entries and check-in notes for safety concerns"
      />
      <Divider />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Settings saved successfully! Changes have been logged to the activity log.
          </Alert>
        )}

        {/* Master Toggle */}
        <Box mb={4}>
          <FormControl component="fieldset" fullWidth>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={handleToggleMainSwitch}
                  color="primary"
                />
              }
              label={
                <Typography variant="subtitle1" fontWeight="medium">
                  Enable Content Safety Screening
                </Typography>
              }
            />
            <FormHelperText>
              When enabled, journal entries and check-in notes will be automatically screened
              for keywords indicating potential safety concerns (self-harm, suicidal ideation).
            </FormHelperText>
          </FormControl>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Content Sources */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            Content Sources
            <Tooltip title="Select which user-generated content should be screened">
              <InfoIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', color: 'info.main' }} />
            </Tooltip>
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default' }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.screenJournalEntries}
                    onChange={handleToggleOption('screenJournalEntries')}
                    disabled={!settings.enabled}
                  />
                }
                label="Screen Journal Entries"
              />
              <FormHelperText sx={{ ml: 4, mt: -1, mb: 1 }}>
                Screen the content of journal entries when clients save them
              </FormHelperText>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.screenCheckInNotes}
                    onChange={handleToggleOption('screenCheckInNotes')}
                    disabled={!settings.enabled}
                  />
                }
                label="Screen Check-In Notes"
              />
              <FormHelperText sx={{ ml: 4, mt: -1 }}>
                Screen the optional notes field in daily check-ins
              </FormHelperText>
            </FormGroup>
          </Paper>
        </Box>

        {/* Alert Configuration */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            Alert Configuration
            <Tooltip title="Configure when safety alerts should be created">
              <InfoIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', color: 'info.main' }} />
            </Tooltip>
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default' }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.createAlertsOnCritical}
                    onChange={handleToggleOption('createAlertsOnCritical')}
                    disabled={!settings.enabled}
                    color="error"
                  />
                }
                label={
                  <Box display="flex" alignItems="center">
                    <WarningAmberIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
                    <Typography>Create alerts for CRITICAL safety flags</Typography>
                  </Box>
                }
              />
              <FormHelperText sx={{ ml: 4, mt: -1, mb: 1 }}>
                Critical keywords include direct references to suicide, self-harm, or specific methods.
                <strong> Recommended: Always enabled.</strong>
              </FormHelperText>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.createAlertsOnModerate}
                    onChange={handleToggleOption('createAlertsOnModerate')}
                    disabled={!settings.enabled}
                    color="warning"
                  />
                }
                label={
                  <Box display="flex" alignItems="center">
                    <WarningAmberIcon color="warning" sx={{ mr: 1, fontSize: 20 }} />
                    <Typography>Create alerts for MODERATE safety flags</Typography>
                  </Box>
                }
              />
              <FormHelperText sx={{ ml: 4, mt: -1, mb: 1 }}>
                Moderate keywords include hopelessness, worthlessness, feeling trapped.
                May generate more alerts - review with clinical team.
              </FormHelperText>

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.notifyTherapistImmediately}
                    onChange={handleToggleOption('notifyTherapistImmediately')}
                    disabled={!settings.enabled}
                  />
                }
                label="Notify assigned therapist immediately when alerts are created"
              />
              <FormHelperText sx={{ ml: 4, mt: -1 }}>
                Sends real-time notification to the client's assigned therapist
              </FormHelperText>
            </FormGroup>
          </Paper>
        </Box>

        {/* Keywords Section */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom>
            Safety Keywords
            <Tooltip title="View default keywords and add custom keywords for screening">
              <InfoIcon fontSize="small" sx={{ ml: 1, verticalAlign: 'middle', color: 'info.main' }} />
            </Tooltip>
          </Typography>

          {/* Critical Keywords Accordion */}
          <Accordion
            expanded={expandedAccordion === 'critical'}
            onChange={handleAccordionChange('critical')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center">
                <WarningAmberIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="subtitle1">
                  Critical Keywords ({defaultKeywords.critical.length} default + {settings.customKeywords.critical.length} custom)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Critical keywords indicate immediate safety concerns. Based on Columbia Protocol and clinical best practices.
              </Typography>

              {/* Default Keywords (Read-only) */}
              <Typography variant="subtitle2" gutterBottom>
                Default Keywords (Read-only)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                {defaultKeywords.critical.map((keyword) => (
                  <Chip
                    key={keyword}
                    label={keyword}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                ))}
              </Box>

              {/* Custom Keywords */}
              <Typography variant="subtitle2" gutterBottom>
                Custom Keywords
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {settings.customKeywords.critical.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No custom critical keywords added
                  </Typography>
                ) : (
                  settings.customKeywords.critical.map((keyword) => (
                    <Chip
                      key={keyword}
                      label={keyword}
                      size="small"
                      color="error"
                      onDelete={() => handleRemoveKeyword('critical', keyword)}
                      deleteIcon={<DeleteIcon />}
                    />
                  ))
                )}
              </Box>

              {/* Add New Keyword */}
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  size="small"
                  placeholder="Add custom keyword"
                  value={newKeyword.critical}
                  onChange={handleKeywordInputChange('critical')}
                  onKeyPress={handleKeywordKeyPress('critical')}
                  disabled={!settings.enabled}
                  sx={{ flexGrow: 1, maxWidth: 300 }}
                />
                <IconButton
                  color="primary"
                  onClick={() => handleAddKeyword('critical')}
                  disabled={!settings.enabled || !newKeyword.critical.trim()}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Moderate Keywords Accordion */}
          <Accordion
            expanded={expandedAccordion === 'moderate'}
            onChange={handleAccordionChange('moderate')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center">
                <WarningAmberIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="subtitle1">
                  Moderate Keywords ({defaultKeywords.moderate.length} default + {settings.customKeywords.moderate.length} custom)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Moderate keywords indicate emotional distress that may warrant attention. Based on PHQ-9 and clinical indicators.
              </Typography>

              {/* Default Keywords (Read-only) */}
              <Typography variant="subtitle2" gutterBottom>
                Default Keywords (Read-only)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
                {defaultKeywords.moderate.map((keyword) => (
                  <Chip
                    key={keyword}
                    label={keyword}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                ))}
              </Box>

              {/* Custom Keywords */}
              <Typography variant="subtitle2" gutterBottom>
                Custom Keywords
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {settings.customKeywords.moderate.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No custom moderate keywords added
                  </Typography>
                ) : (
                  settings.customKeywords.moderate.map((keyword) => (
                    <Chip
                      key={keyword}
                      label={keyword}
                      size="small"
                      color="warning"
                      onDelete={() => handleRemoveKeyword('moderate', keyword)}
                      deleteIcon={<DeleteIcon />}
                    />
                  ))
                )}
              </Box>

              {/* Add New Keyword */}
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  size="small"
                  placeholder="Add custom keyword"
                  value={newKeyword.moderate}
                  onChange={handleKeywordInputChange('moderate')}
                  onKeyPress={handleKeywordKeyPress('moderate')}
                  disabled={!settings.enabled}
                  sx={{ flexGrow: 1, maxWidth: 300 }}
                />
                <IconButton
                  color="primary"
                  onClick={() => handleAddKeyword('moderate')}
                  disabled={!settings.enabled || !newKeyword.moderate.trim()}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Info Box */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>How it works:</strong> When content is saved, it is screened against these keywords.
            Flagged content is marked and, if configured, creates a safety alert for the therapist.
            The AI assistant also receives flagged content with full context to enable appropriate responses.
          </Typography>
        </Alert>

        {/* Save Button */}
        <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            All changes to safety settings are logged in the activity log for audit purposes.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ContentSafetySettings;
